import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, Activity, Users, Flag, IdCard, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { appName } from '../../config/branding';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LINKS = [
  { to: '/admin/status',        label: 'Status',        testId: 'adminnav-link-status',        icon: Activity, countKey: 'status' },
  { to: '/admin/professionals', label: 'Professionals', testId: 'adminnav-link-professionals', icon: Users,    countKey: 'professionals' },
  { to: '/admin/verifications', label: 'Verifications', testId: 'adminnav-link-verifications', icon: IdCard,   countKey: 'verifications' },
  { to: '/admin/lead-reports',  label: 'Lead Reports',  testId: 'adminnav-link-lead-reports',  icon: Flag,     countKey: 'lead_reports' },
];

// How often the badges refresh. Short enough to feel live, long enough to
// avoid thrash. Admins typically have a tab open for minutes at a time.
const BADGE_POLL_MS = 60_000;

// Small coloured bubble next to a nav link's label.
const Badge = ({ count, testId }) => {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid={testId}
      className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#37b34a] text-white text-[10px] font-bold leading-none"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

/**
 * Shared top-nav for every /admin/* page.
 * - Three links across the centre, current page under-dotted in brand green.
 * - Right side shows signed-in admin email + a Sign-out that triggers full logout.
 * - Collapses into a dropdown on mobile.
 */
const AdminNav = ({ activeEmail }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [counts, setCounts] = useState({ status: 0, professionals: 0, lead_reports: 0, verifications: 0 });
  const menuRef = useRef(null);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/nav-counts`, { withCredentials: true });
      setCounts(res.data || {});
    } catch {
      // Silent — badges are a nice-to-have, don't disrupt the nav on failure.
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, BADGE_POLL_MS);
    return () => clearInterval(id);
  }, [fetchCounts]);

  // Also refresh on route change so a visit to Lead Reports clears its badge once reports are resolved.
  useEffect(() => { fetchCounts(); }, [location.pathname, fetchCounts]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const isActive = (to) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <nav
      data-testid="admin-nav"
      className="bg-white border-b border-gray-200 sticky top-0 z-20"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between gap-4">
        {/* Left: wordmark */}
        <Link
          to="/"
          data-testid="adminnav-wordmark"
          className="text-lg font-extrabold text-[#37b34a] no-underline shrink-0"
        >
          {appName}
        </Link>

        {/* Centre: nav links (desktop only) */}
        <div className="hidden md:flex items-center gap-6" data-testid="adminnav-links-desktop">
          {LINKS.map((l) => {
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                aria-current={active ? 'page' : undefined}
                className={`relative text-sm no-underline transition-colors flex items-center ${
                  active ? 'text-gray-900 font-semibold' : 'text-gray-500 hover:text-gray-900 font-medium'
                }`}
              >
                {l.label}
                <Badge count={counts[l.countKey]} testId={`adminnav-badge-${l.countKey}`} />
                {active && (
                  <span className="absolute -bottom-[18px] left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#37b34a]" />
                )}
              </Link>
            );
          })}
        </div>

        {/* Right: email + sign out (desktop) / hamburger (mobile) */}
        <div className="flex items-center gap-3">
          {activeEmail && (
            <span
              data-testid="adminnav-active-email"
              className="hidden sm:inline text-xs text-gray-500 truncate max-w-[200px]"
            >
              {activeEmail}
            </span>
          )}
          <button
            onClick={() => logout()}
            data-testid="adminnav-signout-btn"
            className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>

          {/* Mobile dropdown trigger */}
          <div className="md:hidden relative" ref={menuRef}>
            <button
              data-testid="adminnav-mobile-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
              aria-label="Open admin menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
              <ChevronDown size={14} className="inline ml-1" />
            </button>
            {menuOpen && (
              <div
                data-testid="adminnav-mobile-menu"
                className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
              >
                {LINKS.map((l) => {
                  const active = isActive(l.to);
                  const Icon = l.icon;
                  return (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMenuOpen(false)}
                      className={`flex items-center gap-2 px-4 py-3 text-sm no-underline transition-colors ${
                        active ? 'bg-green-50 text-[#37b34a] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={14} /> {l.label}
                      <Badge count={counts[l.countKey]} testId={`adminnav-mobile-badge-${l.countKey}`} />
                    </Link>
                  );
                })}
                {activeEmail && (
                  <div className="px-4 py-2 border-t border-gray-100 text-[11px] text-gray-500 truncate">
                    {activeEmail}
                  </div>
                )}
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 border-t border-gray-100"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminNav;
